import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {Select} from 'primeng/select';
import {Router} from '@angular/router';
import {AppAuthJWTToken, AppAuthService, AppTokenService} from '@app/auth';
import {AppDialogService, AppMenuService, AppBreakpointsService, AppSidebarService, AppThemeService} from '@app/theme';
import {AppMenuItem} from '@app/theme';
import * as Sentry from '@sentry/angular';
import {ImageCroppedEvent, LoadedImage} from 'ngx-image-cropper';
import {Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';

import {LayoutService} from '../../../@core/utils';
import {ApiService} from '../../../@core/utils/api.service';
import {SessionService} from '../../../@core/utils/session.service';
import {UserAvatarService} from '../../../shared/user-avatar/user-avatar.service';
import {SeasonalTheme, SeasonalThemeService} from '../../season/seasonal-theme.service';

interface RestaurantGroup {
	label: string;
	items: any[];
}

@Component({
	standalone: false,
	selector: 'ngx-header',
	styleUrls: ['./header.component.scss'],
	templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
	@ViewChild('fileInput') fileInput;
	@ViewChild('crop') crop;
	@ViewChild('userMenuPopup') userMenuPopup?: Menu;
	@ViewChild('restaurantSelect') restaurantSelect?: Select;
	@ViewChild('notificationsPanel') notificationsPanel?: ElementRef<HTMLElement>;
	userPictureOnly: boolean = false;
	user: any;
	themes = [
		{
			value: 'default',
			name: 'Light',
		},
		{
			value: 'dark',
			name: 'Dark',
		},
	];
	currentTheme = 'default';
	currentRestaurant = null;
	restaurants: any[] = [];
	restaurantGroups: RestaurantGroup[] = [];
	restaurantList = null;
	currentPermission = null;
	userMenu: AppMenuItem[] = [];
	userMenuItems: MenuItem[] = [];
	imageChangedEvent: any = '';
	croppedImage: any = '';
	private destroy$: Subject<void> = new Subject<void>();
	notificationCenterOpen = false;
	public notifications: any[] = [];
	public displayNotifications: any[] = [];
	private catalogRefreshPending = false;
	seasonPreview: SeasonalTheme | null = null;
	seasonMark: string | null = null;

	constructor(private sessionService: SessionService,
	            private apiService: ApiService,
	            private sidebarService: AppSidebarService,
	            private menuService: AppMenuService,
	            private themeService: AppThemeService,
	            private tokenService: AppTokenService,
	            private layoutService: LayoutService,
	            private router: Router,
	            private dialogService: AppDialogService,
	            private breakpointService: AppBreakpointsService,
	            private authService: AppAuthService,
	            private userAvatarService: UserAvatarService,
	            private seasonalTheme: SeasonalThemeService) {
		this.authService.onTokenChange()
			.subscribe((token: AppAuthJWTToken) => {
				if (token.isValid() && token.getPayload().data) {
					this.user = token.getPayload().data;
					Sentry.setUser(this.user);
					this.userMenu = [
						{title: 'Déconnexion du connect as', hidden: !this.isConnectedAs()},
						{title: 'Changer ma photo de profil'},
						{title: 'Déconnexion', link: '/auth/logout'},
					];
					this.updateUserMenuItems();
				}
			});

		this.currentRestaurant = this.sessionService.getRestaurant();
		this.apiService.getRestaurants().subscribe(value => {
			this.restaurantList = value;
			this.reloadRestaurantList(this.sessionService.getNeededPermissions());
		});
		this.sessionService.getPermissionNeededChangeEmitter().subscribe(value => {
			this.reloadRestaurantList(value, true);
		});
		this.sessionService.getPermissionChangeEmitter().subscribe(() => {
			this.currentPermission = null;
			if (this.catalogRefreshPending) {
				return;
			}
			if (this.restaurantList) {
				this.reloadRestaurantList(this.sessionService.getNeededPermissions(), true);
			}
		});
		this.sessionService.getIdentityChangeEmitter().subscribe(() => {
			this.currentPermission = null;
			this.catalogRefreshPending = true;
			this.refreshRestaurantCatalog(() => {
				this.catalogRefreshPending = false;
				this.sessionService.reapplyRestaurantFromSession();
			});
		});
		this.sessionService.getRestaurantsCatalogRefreshEmitter().subscribe(() => {
			this.refreshRestaurantCatalog();
		});
		this.loadNotifications();
		setInterval(() => {
			this.loadNotifications();
		}, 60000);
	}

	private loadNotifications(): void {
		this.apiService.getNotifications().subscribe(value => {
			this.notifications = value ?? [];
			this.displayNotifications = this.groupTicketNotifications(this.notifications);
		});
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.notificationCenterOpen || !this.notificationsPanel?.nativeElement) {
			return;
		}
		if (!this.notificationsPanel.nativeElement.contains(event.target as Node)) {
			this.notificationCenterOpen = false;
		}
	}

	public toggleNotificationCenter(event: Event): void {
		event.stopPropagation();
		this.notificationCenterOpen = !this.notificationCenterOpen;
	}

	private groupTicketNotifications(notifications: any[]): any[] {
		const ticketMap = new Map<string, any>();
		const others: any[] = [];

		for (const notification of notifications) {
			if (notification.category === 'ticket' && notification.type != null) {
				const key = String(notification.type);
				const existing = ticketMap.get(key);
				if (!existing) {
					ticketMap.set(key, {
						...notification,
						_groupedIds: [notification.id],
					});
					continue;
				}
				ticketMap.set(key, this.mergeTicketNotification(existing, notification));
				continue;
			}
			others.push(notification);
		}

		return [...ticketMap.values(), ...others].sort((a, b) =>
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
		);
	}

	private mergeTicketNotification(primary: any, extra: any): any {
		const newer = new Date(extra.created_at) > new Date(primary.created_at) ? extra : primary;
		const groupedIds = [...new Set([...(primary._groupedIds ?? []), ...(extra._groupedIds ?? [primary.id]), extra.id])];
		const groupedContent = `Ticket #${newer.type}<br>Nouveaux messages sur ce ticket.`;
		const content = groupedIds.length > 1 ? groupedContent : newer.content;

		return {
			...newer,
			content,
			_groupedIds: groupedIds,
		};
	}

	private getNotificationSiblings(notification: any): any[] {
		if (notification.category !== 'ticket' || notification.type == null) {
			return [notification];
		}
		return this.notifications.filter(item =>
			item.category === 'ticket' && String(item.type) === String(notification.type),
		);
	}

	ngOnInit() {
		this.currentTheme = this.themeService.currentTheme;
		const {xl} = this.breakpointService.getBreakpointsMap();
		this.menuService.onItemSelect()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.sidebarService.compact('menu-sidebar');
			});

		this.themeService.onMediaQueryChange()
			.pipe(
				map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
				takeUntil(this.destroy$),
			)
			.subscribe((isLessThanXl: boolean) => this.userPictureOnly = isLessThanXl);

		this.themeService.onThemeChange()
			.pipe(
				map(({name}) => name),
				takeUntil(this.destroy$),
			)
			.subscribe(themeName => this.currentTheme = themeName);

		this.seasonalTheme.chrome$
			.pipe(takeUntil(this.destroy$))
			.subscribe((chrome) => {
				this.seasonPreview = chrome.preview;
				this.seasonMark = chrome.mark;
			});
	}

	quitSeasonPreview(): void {
		this.seasonalTheme.clearPreview();
	}

	ngOnDestroy() {
		this.clearNotificationClickGuard();
		this.destroy$.next();
		this.destroy$.complete();
	}

	changeTheme(themeName: string) {
		localStorage.setItem('theme', themeName);
		this.themeService.changeTheme(themeName);
	}

	openHelpCenter(): void {
		void this.router.navigate(['/pages/aide']);
	}

	toggleSidebar(event?: Event): boolean {
		event?.preventDefault();
		event?.stopPropagation();
		this.sidebarService.toggle(true, 'menu-sidebar');
		this.layoutService.changeLayoutSize();
		return false;
	}

	get showMenuToggle(): boolean {
		return !this.sidebarService.isTabletSidebarViewport();
	}

	navigateHome() {
		this.menuService.navigateHome();
		return true;
	}

	changeRestaurant(restaurant: any): void {
		this.currentRestaurant = this.resolveRestaurant(restaurant);
		this.sessionService.setRestaurant(this.currentRestaurant);
	}

	onRestaurantSelect(event: { value?: unknown }): void {
		this.changeRestaurant(event?.value ?? null);
	}

	enableRestaurantPicker(): boolean {
		return this.getUserRestaurantCount() > 0;
	}

	showRestaurantContext(): boolean {
		return this.getUserRestaurantCount() > 0;
	}

	get currentRestaurantName(): string {
		return this.currentRestaurant?.nom_court
			|| this.sessionService.getRestaurant()?.nom_court
			|| '';
	}

	onRestaurantBarClick(event: Event): void {
		if (!this.enableRestaurantPicker()) {
			return;
		}
		if (event instanceof KeyboardEvent && event.key === ' ') {
			event.preventDefault();
		}
		this.openRestaurantSelect(event);
	}

	openRestaurantSelect(event?: Event): void {
		event?.stopPropagation();
		const select = this.restaurantSelect;
		if (!select) {
			return;
		}
		select.show(true);
	}

	private refreshRestaurantCatalog(onLoaded?: () => void): void {
		this.apiService.getRestaurants().subscribe(value => {
			this.restaurantList = value;
			this.reloadRestaurantList(this.sessionService.getNeededPermissions(), true);
			onLoaded?.();
		});
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files?.length) {
			return;
		}
		this.imageChangedEvent = event;
		this.dialogService.open(this.crop);
	}

	private openProfilePhotoPicker(): void {
		const input = this.fileInput?.nativeElement as HTMLInputElement | undefined;
		if (!input) {
			return;
		}
		input.value = '';
		input.click();
	}

	private onUserMenuAction(entry: AppMenuItem): void {
		if (entry.link) {
			void this.router.navigateByUrl(entry.link);
			return;
		}
		if (entry.title === 'Déconnexion du connect as') {
			this.tokenService.set(new AppAuthJWTToken(this.user.previous_token, 'email'));
			void this.router.navigate(['/']);
			return;
		}
		if (entry.title === 'Changer ma photo de profil') {
			this.openProfilePhotoPicker();
		}
	}

	public uploadProfilePic(ref): void {
		this.apiService.addProfilePic({
			image: this.croppedImage,
		}).subscribe(value => {
			ref.close();
			this.userAvatarService.invalidate(this.user?.id);
			// window.location.reload();
		});
	}

	fileChangeEvent(event: any): void {
		this.imageChangedEvent = event;
	}

	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64;
	}

	imageLoaded(image: LoadedImage) {
		// show cropper
	}

	cropperReady() {
		// cropper ready
	}

	loadImageFailed() {
		// show message
	}

	private isConnectedAs(): boolean {
		return this.user && this.user.previous_token;
	}

	private reloadRestaurantList(permissionName: string | null, force = false) {
		if (!force && permissionName && permissionName === this.currentPermission) {
			return;
		}
		if (!this.restaurantList || !this.sessionService.getPermissions() || this.sessionService.getPermissions().length < 1) {
			return;
		}
		this.currentPermission = permissionName;
		// Picker : tous les restaurants rattachés (rôles différents par resto, ex. extra + gérant).
		this.restaurants = this.buildRestaurantsFromPermissions(null);
		this.restaurantGroups = this.buildRestaurantGroups(this.restaurants);

		if (this.restaurants.length === 1) {
			const onlyRestaurant = this.restaurants[0];
			if (this.restaurantId(this.currentRestaurant) !== this.restaurantId(onlyRestaurant)) {
				this.changeRestaurant(onlyRestaurant);
			} else {
				this.currentRestaurant = onlyRestaurant;
			}
			return;
		}

		const currentId = this.restaurantId(this.currentRestaurant);
		if (currentId == null || !this.restaurants.some((resto) => this.restaurantId(resto) === currentId)) {
			this.changeRestaurant(null);
			return;
		}

		this.currentRestaurant = this.restaurants.find((resto) => this.restaurantId(resto) === currentId) ?? null;
	}

	private getUserRestaurantCount(): number {
		if (!this.restaurantList || !this.sessionService.getPermissions()?.length) {
			return this.restaurants?.length ?? 0;
		}
		return this.buildRestaurantsFromPermissions(null).length;
	}

	private buildRestaurantsFromPermissions(permissionName: string | null): any[] {
		const seenIds = new Set<number>();
		const restaurants: any[] = [];

		for (const permission of this.sessionService.getPermissions()) {
			if (permissionName && permission[permissionName] !== 1) {
				continue;
			}
			const restaurantId = parseInt(String(permission['restaurant_id']), 10);
			if (Number.isNaN(restaurantId) || seenIds.has(restaurantId)) {
				continue;
			}
			const restaurant = this.restaurantList.find(
				(resto) => parseInt(String(resto.id), 10) === restaurantId,
			);
			if (!restaurant) {
				continue;
			}
			seenIds.add(restaurantId);
			restaurants.push(restaurant);
		}

		return restaurants.sort((a, b) => this.sortRestaurantsByName(a, b));
	}

	private restaurantId(restaurant: any): number | null {
		if (!restaurant || restaurant.id == null) {
			return null;
		}
		return parseInt(String(restaurant.id), 10);
	}

	private isRestaurantOpen(restaurant: any): boolean {
		return restaurant?.ouvert === 1 || restaurant?.ouvert === '1';
	}

	private sortRestaurantsByName(a: any, b: any): number {
		return (a?.nom_court ?? '').localeCompare(b?.nom_court ?? '', 'fr', {sensitivity: 'base'});
	}

	private buildRestaurantGroups(restaurants: any[]): RestaurantGroup[] {
		const groups: RestaurantGroup[] = [];

		const succursales = restaurants
			.filter(restaurant => restaurant.type === 'succursale')
			.sort((a, b) => this.sortRestaurantsByName(a, b));
		if (succursales.length) {
			groups.push({label: 'Succursales', items: succursales});
		}

		const franchises = restaurants
			.filter(restaurant => restaurant.type !== 'succursale' && this.isRestaurantOpen(restaurant))
			.sort((a, b) => this.sortRestaurantsByName(a, b));
		if (franchises.length) {
			groups.push({label: 'Franchises', items: franchises});
		}

		const nonOuvert = restaurants
			.filter(restaurant => restaurant.type !== 'succursale' && !this.isRestaurantOpen(restaurant))
			.sort((a, b) => this.sortRestaurantsByName(a, b));
		if (nonOuvert.length) {
			groups.push({label: 'Non ouvert', items: nonOuvert});
		}

		return groups;
	}

	private resolveRestaurant(restaurant: any): any {
		if (!restaurant) {
			return null;
		}
		const id = this.restaurantId(restaurant);
		if (id == null || !this.restaurants?.length) {
			return restaurant;
		}
		return this.restaurants.find((resto) => this.restaurantId(resto) === id) ?? restaurant;
	}

	public clearNotifications(): void {
		for (const notification of [...this.displayNotifications]) {
			this.deleteNotification(notification, false);
		}
		this.notificationCenterOpen = false;
	}

	public deleteNotification(notification: any, keepPanelOpen = true): void {
		const siblings = notification._groupedIds?.length
			? this.notifications.filter(item => notification._groupedIds.includes(item.id))
			: this.getNotificationSiblings(notification);
		let remaining = siblings.length;
		const onDeleted = () => {
			remaining--;
			if (remaining > 0) {
				return;
			}
			this.loadNotifications();
			if (!keepPanelOpen) {
				this.notificationCenterOpen = false;
			}
		};

		for (const item of siblings) {
			this.apiService.deleteNotification(item.id).subscribe(() => {
				this.notifications = this.notifications.filter(notif => notif.id !== item.id);
				onDeleted();
			});
		}
	}

	public onNotificationClick(event: MouseEvent, notification: any): void {
		event.preventDefault();
		event.stopPropagation();
		if (this.shouldOpenInNewTab(event)) {
			this.openNotificationInNewTab(notification);
			return;
		}
		this.viewNotification(notification);
	}

	private shouldOpenInNewTab(event: MouseEvent): boolean {
		return event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1;
	}

	private openNotificationInNewTab(notification: any): void {
		const link = this.getLink(notification);
		if (!link) {
			return;
		}
		if (link.startsWith('http')) {
			window.open(link, '_blank', 'noopener,noreferrer');
			return;
		}
		const url = this.router.serializeUrl(this.router.parseUrl(link));
		window.open(url, '_blank');
	}

	public viewNotification(notification: any): void {
		const link = this.getLink(notification);
		if (!link) {
			return;
		}
		this.armNotificationClickGuard();
		window.setTimeout(() => {
			this.notificationCenterOpen = false;
		});
		if (link.startsWith('http')) {
			window.open(link, '_blank');
			this.dismissNotificationSiblings(notification);
			return;
		}
		void this.router.navigateByUrl(link).then((navigated) => {
			if (!navigated) {
				return;
			}
			window.setTimeout(() => {
				if (!this.isOnNotificationTarget(link)) {
					return;
				}
				this.dismissNotificationSiblings(notification);
			}, 800);
		});
	}

	private notificationClickGuard?: () => void;

	private armNotificationClickGuard(): void {
		this.clearNotificationClickGuard();
		const block = (event: Event) => {
			event.preventDefault();
			event.stopPropagation();
		};
		document.addEventListener('click', block, true);
		const timer = window.setTimeout(() => this.clearNotificationClickGuard(), 1200);
		this.notificationClickGuard = () => {
			document.removeEventListener('click', block, true);
			window.clearTimeout(timer);
		};
	}

	private clearNotificationClickGuard(): void {
		this.notificationClickGuard?.();
		this.notificationClickGuard = undefined;
	}

	private isOnNotificationTarget(link: string): boolean {
		const currentPath = this.router.url.split('?')[0].split('#')[0];
		const targetPath = link.split('?')[0].split('#')[0];
		return currentPath === targetPath;
	}

	openUserMenu(event: Event): void {
		if (!this.userMenuItems.length || !this.userMenuPopup) {
			return;
		}
		this.userMenuPopup.toggle(event);
		event.stopPropagation();
	}

	private updateUserMenuItems(): void {
		this.userMenuItems = (this.userMenu || [])
			.filter((entry) => !entry.hidden)
			.map((entry) => ({
				label: entry.title,
				command: () => this.onUserMenuAction(entry),
			}));
	}

	public getLink(notification: any): string {
		if (notification.category === 'ticket') {
			return '/pages/ticket/' + notification.type + '/detail';
		}
		if (notification.category === 'reservation_event' && notification.url) {
			return notification.url;
		}
		if (notification.url) {
			return notification.url;
		}
		return null;
	}

	private dismissNotificationSiblings(notification: any): void {
		const siblings = notification._groupedIds?.length
			? this.notifications.filter(item => notification._groupedIds.includes(item.id))
			: this.getNotificationSiblings(notification);
		let remaining = siblings.length;
		const onDismissed = () => {
			remaining--;
			if (remaining > 0) {
				return;
			}
			this.loadNotifications();
		};

		for (const item of siblings) {
			this.apiService.deleteNotification(item.id).subscribe(() => {
				this.notifications = this.notifications.filter(notif => notif.id !== item.id);
				onDismissed();
			});
		}
	}
}
